import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { CardBody, Card } from "reactstrap";
// components
import ALoader from "views/pages/manage/common/a-loader";
import DepositedList from "../components/DepositedList";
import DepositSuccessContent from "modules/paymentManagement/cash-management/receipt/components/DepositSuccessContent";
import StudentSearchBar from "modules/paymentManagement/cash-management/components/StudentSearchBar";
import TopHeaderBar from "modules/paymentManagement/cash-management/components/TopHeaderBar";
import DocumentViewer from "modules/paymentManagement/cash-management/receipt/components/DocumentViewerModal";
import DepositedReceiptFiltersDrawer from "../components/DepositedReceiptFiltersDrawer";
// hooks
import { useFetchList } from "modules/paymentManagement/cash-management/hooks/useFetchList";
import { getChequeReceiptListApi } from "modules/paymentManagement/cheque-management/receipts/api";
import { updateRequest } from "../../../../../views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { useModal } from "../../../../../contexts/customModal";
import { useFetchDropdown } from "modules/paymentManagement/cash-management/receipt/hooks/useDropdown";
import { useDepositCheque } from "../../hooks/useDepositCheque";

import { useNavigateToGivenPath } from "views/pages/manage/edpOps/studentDetails/paymentSummary/service";
// helpers
import { getFilterDate } from "modules/paymentManagement/approvalDashboard/shared/constant";

import "modules/paymentManagement/cash-management/styles/cashManagement.scss";
import SuccessMessageAlert from "../components/SuccessMessageAlert";
import ChequeDateExtensionForm from "../components/ChequeDateExtensionForm";
import { useBuTypeNameByBuId } from "modules/paymentManagement/cash-management/hooks/useFetchBuType";
const SEARCH_STYLE = { fontSize: "12px" };
const SEARCH_BY = [
  { key: "psid", value: "By PSID" },
  { key: "referenceNumber", value: "By Cheque Number" },
];

// Defined outside to prevent re-creation
const ViewChequeDepositPage = () => {
  // 1. URL & Navigation
  const { id: businessAreaId, cashierOffice } = useParams();
  const navigate = useNavigateToGivenPath();
  const [viewer, setViewer] = useState({
    open: false,
    s3key: "",
  });

  // Memoize navigation to prevent recreation
  const goViewDeposit = useCallback(
    () =>
      navigate(
        `/admin/cheque-management/viewChequeDeposit/${businessAreaId}/${cashierOffice}`
      ),
    [navigate, businessAreaId, cashierOffice]
  );

  const goBackToChequeManagement = () => {
    navigate(`/admin/cheque-management`);
  };

  const onEditReceipt = (row) => {
    showModal({
      title: `Cheque date extension`,
      content: (
        <ChequeDateExtensionForm refreshList={refreshReceiptList} row={row} />
      ),
      maxWidth: "lg",
    });
  };

  // 2. Data Fetching
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const dropdownValues = useFetchDropdown();
  const { depositCheque, loading: isSubmitting } =
    useDepositCheque(setIsDropdownLoading);

  // 3. List Management
  const [searchInput, setSearchInput] = useState("");
  const [searchBy, setSearchBy] = useState("psid");

  const {
    rows,
    loading: isListLoading,
    pagination,
    onPageChange,
    onPageSizeChange,
    filters,
    onFilterApply,
    fetchTrackerTimeLine,
  } = useFetchList(getChequeReceiptListApi, {
    initialPageSize: 100, // Consider lowering this
    businessAreaId,
    cashierOffice,
    statusFilters: ["DEPOSITED"],
    initialFilters: {},
  });

  const updatedPagination = useMemo(
    () => ({
      ...pagination,
      page: pagination.page + 1, // Adjusting for 1-based UI
    }),
    [pagination]
  );

  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [pagination.page]);

  // 5. Modal & Form Logic
  const { showModal, hideModal } = useModal();

  const refreshReceiptList = useCallback(
    (response, row) => {
      onFilterApply({});
      setSelectedIds(new Set());

      showModal({
        title: `Cheque/DD status updated to bounced`,
        content: (
          <SuccessMessageAlert
            onClose={hideModal}
            details={response}
            row={row}
            onView={goViewDeposit}
            isChequeBounced={true}
          />
        ),
        maxWidth: "lg",
      });
    },
    [onFilterApply, showModal, goViewDeposit, hideModal]
  );

  // 6. Filter Handlers
  const applyFilterHandler = useCallback(
    (newFilter) => {
        const formateRequest = {
              ...newFilter,
              chequeDateFrom: getFilterDate(newFilter?.chequeDateFrom, "start"),
              chequeDateTo: getFilterDate(newFilter?.chequeDateTo, "end"),
            };
      onFilterApply(updateRequest(formateRequest));
    },
    [onFilterApply]
  );

  const applyPrimaryFilter = useCallback(() => {
    onFilterApply({ [searchBy]: searchInput });
  }, [onFilterApply, searchBy, searchInput]);

  const removeFilterFormHandler = useCallback(() => {
    onFilterApply({});
  }, [onFilterApply]);

  const { buTypeName, loading } = useBuTypeNameByBuId(businessAreaId)
  // 7. Render Helpers
  const headerData = useMemo(
    () => ({
      Type: buTypeName,
      BU: businessAreaId,
      "Cashier's office": cashierOffice,
    }),
    [businessAreaId, cashierOffice,buTypeName]
  );

  const onViewCheque = (row) => {
    setViewer({ open: true, s3key: row?.attachmentS3Key });
  };

  const statusChipChangeHandler = (value) => {
    if (value === "all") {
      applyFilterHandler({ status: [] });
      return;
    }
    applyFilterHandler({ status: [value] });
  };

  return (
    <>
      <div className="remove-container-top-space">
        <TopHeaderBar
          title="List of all cheque collection"
          headerData={headerData}
          onBackClick={goBackToChequeManagement}
        />

        <div className="cm-container">
          {(isSubmitting || isListLoading || isDropdownLoading) && (
            <ALoader position="fixed" />
          )}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between mb-3">
                <div className="heading-3 color-black semi-bold">
                  Cheque list{" "}
                </div>

                <div className="d-flex gap-md">
                  <div>
                    <StudentSearchBar
                      searchValue={searchInput}
                      onChange={setSearchInput}
                      onKeyDown={applyPrimaryFilter}
                      onSearchClick={applyPrimaryFilter}
                      dropdownValue={searchBy}
                      onDropdownChange={setSearchBy}
                      dropdownStyles={SEARCH_STYLE}
                      dropdownOptions={SEARCH_BY}
                    />
                  </div>

                  <DepositedReceiptFiltersDrawer
                    applyFilterHandler={applyFilterHandler}
                    appliedForm={filters}
                    isHideChequeFilters={true}
                    dropdownValues={dropdownValues}
                    onRemoveFilterHandler={removeFilterFormHandler}
                  />
                </div>
              </div>

              <DepositedList
                rows={rows}
                loading={isListLoading}
                pagination={updatedPagination}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onViewCheque={onViewCheque}
                openModal={showModal}
                hideModal={hideModal}
                onEditReceipt={onEditReceipt}
                refreshList={refreshReceiptList}
                fetchTrackerTimeLine={fetchTrackerTimeLine}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      <DocumentViewer
        open={viewer.open}
        onClose={() => setViewer({ open: false, s3key: "" })}
        s3Key={viewer.s3key}
      />
    </>
  );
};

export default ViewChequeDepositPage;
